// src/studio/avatar/services/avatarApi.js

import api from '../../../api';

const BASE_PATH = '/studio/avatars';

function creationPath(id) {
  if (typeof id !== 'string' || !/^[a-f0-9]{24}$/i.test(id)) {
    throw new Error('Invalid avatar creation ID.');
  }

  return `${BASE_PATH}/${id}`;
}

function appendImage(form, image) {
  if (!(image instanceof Blob) || image.type !== 'image/png') {
    throw new Error('Export the avatar as a PNG before saving.');
  }

  form.append('image', image, 'avatar.png');
}

// Override api.js's JSON default.
// Axios/browser will supply the multipart boundary.
const multipartOptions = {
  headers: {
    'Content-Type': undefined,
  },
};

export async function listAvatarCreations({
  limit = 20,
  cursor,
} = {}) {
  const { data } = await api.get(BASE_PATH, {
    params: { limit, cursor },
  });

  return data;
}

export async function getAvatarCreation(id) {
  const { data } = await api.get(creationPath(id));
  return data.creation;
}

export async function createAvatarCreation({
  name,
  config,
  image,
}) {
  const form = new FormData();

  form.append('name', name);
  form.append('config', JSON.stringify(config));
  appendImage(form, image);

  const { data } = await api.post(
    BASE_PATH,
    form,
    multipartOptions
  );

  return data.creation;
}

export async function updateAvatarCreation(
  id,
  { name, config, image, revision }
) {
  if (!Number.isSafeInteger(revision) || revision < 0) {
    throw new Error('Reload this creation before saving changes.');
  }

  const changesDesign =
    config !== undefined || image !== undefined;

  if (changesDesign) {
    if (config === undefined || image === undefined) {
      throw new Error('Design changes require configuration and PNG.');
    }

    const form = new FormData();

    form.append('revision', String(revision));
    form.append('config', JSON.stringify(config));

    if (name !== undefined) {
      form.append('name', name);
    }

    appendImage(form, image);

    const { data } = await api.patch(
      creationPath(id),
      form,
      multipartOptions
    );

    return data.creation;
  }

  if (name === undefined) {
    throw new Error('No changes provided.');
  }

  const { data } = await api.patch(creationPath(id), {
    name,
    revision,
  });

  return data.creation;
}

export function renameAvatarCreation(id, { name, revision }) {
  return updateAvatarCreation(id, { name, revision });
}

export async function deleteAvatarCreation(id) {
  const { data } = await api.delete(creationPath(id));
  return data;
}

export async function applyAvatarCreation(id) {
  const { data } = await api.post(
    `${creationPath(id)}/apply`,
    {}
  );

  // Contains changed user fields; merge into the current user.
  return data;
}
